import api from 'services/api';
import { type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

import { schedulingWindowSchema, type SchedulingWindow } from './getSchedulingWindow';
import type { SchedulingWindowReference, SchedulingWindowSchedule } from './types';

/**
 * The FE-authored request body for creating or updating a scheduling window.
 *
 * **Permissive on purpose** — the server owns the rules (name, timezone and
 * zone_name required; the folder must live in the zone; per-schedule field ranges;
 * no reference cycles) and answers 400. One shape for both writes: the URL
 * carries the identity on PUT, so a caller thinks in terms of "the window's
 * fields" either way.
 *
 * Fields are required-presence with permissive values rather than optional, so a
 * `toSave()` projection that forgets one fails to compile. `folder_id: null`
 * clears the folder and is always sent.
 *
 * `exclusions` is deliberately absent: the backend still merges a legacy
 * `exclusions` array into `references`, but nothing should author one any more.
 */
export interface SaveSchedulingWindow {
  readonly name: string | null;
  readonly description: string | null;
  readonly timezone: string | null;
  readonly zone_name: string | null;
  readonly folder_id: number | null;
  readonly schedules: readonly SchedulingWindowSchedule[];
  readonly references: readonly SchedulingWindowReference[];
}

/**
 * POST `/scheduling_windows`. Returns the created window, serialized through the
 * same `windowDetailsSQL()` as the single read — so the response is validated
 * against the shared `schedulingWindowSchema` rather than a redeclared one.
 * Rejects on HTTP error — the write did not happen, and the server's complaint
 * is the useful thing to report. Answers with the **unreadable** arm when the
 * write DID happen and its receipt cannot be read: by then the server has
 * committed, so that is not a failed save and must not be reported as one
 * (#1035).
 */
export const postSchedulingWindow = async (save: SaveSchedulingWindow): Promise<Received<SchedulingWindow>> => {
  const res = await api.post('/scheduling_windows', save);
  return readResponse(res.data, { schema: schedulingWindowSchema, source: 'postSchedulingWindow' });
};
